import { DatePipe } from '@angular/common';
import { Component, computed, inject, OnInit, output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { captureException } from '@sentry/angular';
import { StoreHistoryService, StoreSnapshot } from 'src/app/services/store-history/store-history.service';
import { StoreHistoryTelemetryService } from 'src/app/services/store-history/store-history-telemetry.service';
import type { RestoreDialogData } from '../restore-dialog/restore-dialog.component';

type SnapshotsState = 'loading' | 'error' | 'empty' | 'ready';

const SKELETON_ROWS = [0, 1, 2];

/** The collections a row summarises, in the order the row reads them out. */
const SUMMARISED = [
  { collection: 'charts', key: 'store-history.x-charts' },
  { collection: 'chart_tables', key: 'store-history.x-table-charts' },
  { collection: 'chart_product_rels', key: 'store-history.x-assignments' },
] as const;

@Component({
  templateUrl: './snapshots-tab.component.html',
  standalone: true,
  selector: 'app-history-snapshots-tab',
  imports: [DatePipe, MatButtonModule, MatIconModule, TranslatePipe],
})
export class HistorySnapshotsTabComponent implements OnInit {
  private readonly storeHistory = inject(StoreHistoryService);
  private readonly dialog = inject(MatDialog);
  private readonly telemetry = inject(StoreHistoryTelemetryService);

  readonly interacted = output<void>();

  readonly skeletonRows = SKELETON_ROWS;
  readonly summarised = SUMMARISED;

  private readonly loading = signal(true);
  private readonly failed = signal(false);

  readonly snapshots = computed(() => this.storeHistory.snapshots());

  readonly state = computed<SnapshotsState>(() => {
    if (this.loading()) return 'loading';
    if (this.failed()) return 'error';
    if (!this.snapshots().length) return 'empty';

    return 'ready';
  });

  ngOnInit(): void {
    void this.load();
  }

  async load(): Promise<void> {
    this.loading.set(true);
    this.failed.set(false);

    try {
      await this.storeHistory.loadSnapshots();
    } catch (error) {
      // Distinct from "you have no snapshots yet" — one is a story about the store, the other is a
      // story about the network, and showing the wrong one sends the merchant to support.
      captureException(error);
      this.failed.set(true);
    } finally {
      this.loading.set(false);
    }

    this.telemetry.snapshotViewed(this.snapshots().length, this.oldestAgeDays());
  }

  /** How far back the list actually reaches, which is not the same as the retention number. */
  private oldestAgeDays(): number | undefined {
    const oldest = this.snapshots()[this.snapshots().length - 1];

    if (!oldest) return undefined;

    return Math.floor((Date.now() - oldest.takenAt.getTime()) / 86_400_000);
  }

  triggerKey(snapshot: StoreSnapshot): string {
    return `store-history.trigger.${snapshot.trigger}`;
  }

  count(snapshot: StoreSnapshot, collection: string): number {
    return snapshot.counts[collection] ?? 0;
  }

  async onRestore(snapshot: StoreSnapshot): Promise<void> {
    this.interacted.emit();

    const component = await import('../restore-dialog/restore-dialog.component').then(
      (module) => module.RestoreDialogComponent,
    );

    this.dialog.open<typeof component.prototype, RestoreDialogData>(component, {
      data: { snapshot },
      width: '520px',
      // The merchant must not be able to dismiss a restore that is already running; the dialog
      // takes this back over once it starts.
      disableClose: false,
    });
  }
}
